import React, { type ComponentType, type ReactNode } from 'react';

import { ReloadOutlined, HomeOutlined } from '@ant-design/icons';
import { Result, Button } from 'antd';
import { ErrorBoundary as ReactErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

interface DefaultErrorFallbackProps extends FallbackProps {
  showDetails?: boolean;
  homeUrl: string;
}

// 默認的錯誤 Fallback 組件
const DefaultErrorFallback = ({ error, resetErrorBoundary, showDetails = false, homeUrl }: DefaultErrorFallbackProps) => {
  const { t } = useTranslation();

  const handleGoHome = () => {
    window.location.href = homeUrl;
  };

  return (
    <div style={{ padding: '50px', textAlign: 'center' }}>
      <Result
        status="error"
        title={t('common.error_boundary.title')}
        subTitle={t('common.error_boundary.subtitle')}
        extra={[
          <Button
            type="primary"
            key="reload"
            icon={<ReloadOutlined />}
            onClick={resetErrorBoundary}
          >
            {t('common.error_boundary.reload_button')}
          </Button>,
          <Button
            key="home"
            icon={<HomeOutlined />}
            onClick={handleGoHome}
          >
            {t('common.error_boundary.home_button')}
          </Button>,
        ]}
      />

      {showDetails && Boolean(error) && (
        <details style={{
          marginTop: '20px',
          textAlign: 'left',
          backgroundColor: '#f5f5f5',
          padding: '10px',
          borderRadius: '4px',
          maxWidth: '800px',
          margin: '20px auto 0'
        }}>
          <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
            {t('common.error_boundary.error_details')}
          </summary>
          <pre style={{
            whiteSpace: 'pre-wrap',
            fontSize: '12px',
            marginTop: '10px',
            color: '#666'
          }}>
            {String(error)}
            {error instanceof Error ? error.stack : null}
          </pre>
        </details>
      )}
    </div>
  );
};

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ComponentType<FallbackProps>;
  showDetails?: boolean;
  homeUrl: string;
  onError?: (error: unknown, errorInfo: React.ErrorInfo) => void;
}

// 使用 functional component 的錯誤邊界組件
const ErrorBoundary = ({
  children,
  fallback: CustomFallback,
  showDetails = false,
  homeUrl,
  onError,
  ...props
}: ErrorBoundaryProps) => {
  const handleError = (error: unknown, errorInfo: React.ErrorInfo) => {
    // 記錄錯誤到控制台
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // 這裡可以添加錯誤報告服務，例如 Sentry
    // reportError(error, errorInfo);

    // 如果提供了自定義的錯誤處理函數，調用它
    if (onError) {
      onError(error, errorInfo);
    }
  };

  // 如果提供了自定義 fallback，使用它；否則使用默認的
  const FallbackComponent = CustomFallback ||
    ((fallbackProps: FallbackProps) => <DefaultErrorFallback {...fallbackProps} showDetails={showDetails} homeUrl={homeUrl} />);

  return (
    <ReactErrorBoundary
      FallbackComponent={FallbackComponent}
      onError={handleError}
      {...props}
    >
      {children}
    </ReactErrorBoundary>
  );
};

export default ErrorBoundary;
